import {
	Alert,
	Anchor,
	Button,
	Group,
	Skeleton,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { Link, useParams } from "@tanstack/react-router";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import { LogConversationCard } from "./log-conversation-card";
import { LogFilters } from "./log-filters";
import { LogUserProfile } from "./log-user-profile";
import { useAssistantLogUser } from "./use-assistant-log-user";

/**
 * Lapis 2 (`/admin/ai-assistant/log/$userId`): profil pengguna + angka ringkas
 * + daftar kartu percakapannya. Grafik "Aktivitas 30 hari" belum ada di versi
 * ini — tercatat sebagai task terbuka (rancangan 08 §8).
 */
export function LogUserPage() {
	const { userId } = useParams({ from: "/admin/ai-assistant/log/$userId/" });
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const td = assistantLogDetailTexts[lang];
	const state = useAssistantLogUser(userId);

	return (
		<Stack gap="lg">
			<Stack gap={4}>
				<Anchor component={Link} to="/admin/ai-assistant/log" size="sm">
					{td.backToUsers}
				</Anchor>
				<Title order={3}>{t.title}</Title>
			</Stack>

			{state.notFound && (
				<Alert color="yellow" icon={<IconAlertCircle size={16} />}>
					{td.userNotFound}
				</Alert>
			)}
			{state.error && (
				<Alert color="red" icon={<IconAlertCircle size={16} />}>
					{state.error}
				</Alert>
			)}

			{state.loading ? (
				<Skeleton height={260} radius="md" data-log-user-loading />
			) : (
				state.detail && (
					<>
						<LogUserProfile
							profile={state.detail.profile}
							totals={state.detail.totals}
						/>

						<LogFilters
							filters={state.filters}
							setFilters={state.setFilters}
							search={state.search}
							setSearch={state.setSearch}
							searchPlaceholder={td.searchConversation}
						/>

						<Stack gap="sm">
							<Title order={4}>{td.conversationsTitle}</Title>
							{state.conversations.length === 0 ? (
								<Text size="sm" c="dimmed" data-log-conversations-empty>
									{td.conversationsEmpty}
								</Text>
							) : (
								state.conversations.map((conversation) => (
									<LogConversationCard
										key={conversation.id}
										userId={userId}
										conversation={conversation}
									/>
								))
							)}
							{state.nextCursor && (
								<Group>
									<Button
										variant="light"
										size="xs"
										loading={state.loadingMore}
										onClick={state.loadMore}
									>
										{td.loadMore}
									</Button>
								</Group>
							)}
						</Stack>
					</>
				)
			)}
		</Stack>
	);
}
